import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ClipboardCheck } from 'lucide-react';
import MeetingWorkspace from './MeetingWorkspace';
import { getProfile, isSupabaseConfigured, loadStoredSession } from './supabaseClient';

function MeetingPortal() {
  const [open, setOpen] = useState(false);
  const [tabStrip, setTabStrip] = useState(null);
  const [allowed, setAllowed] = useState(!isSupabaseConfigured);

  useEffect(() => {
    if (!isSupabaseConfigured) return undefined;
    let cancelled = false;
    let attempts = 0;

    const resolveAccess = async () => {
      const session = loadStoredSession();
      if (!session?.access_token || !session?.user?.id) {
        attempts += 1;
        if (!cancelled && attempts < 20) window.setTimeout(resolveAccess, 150);
        return;
      }

      try {
        const profile = await getProfile(session.access_token, session.user.id);
        if (!cancelled) setAllowed(['admin', 'internal', 'model'].includes(profile?.role));
      } catch {
        if (!cancelled) setAllowed(false);
      }
    };

    resolveAccess();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    let attempts = 0;

    const findTarget = () => {
      if (cancelled) return;
      const target = document.querySelector('.tab-strip');
      if (target) {
        setTabStrip(target);
        return;
      }
      attempts += 1;
      if (attempts < 30) window.setTimeout(findTarget, 100);
    };

    findTarget();
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!allowed) return null;

  const button = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      style={{ order: 15 }}
      className="tab-button px-4 py-2 rounded-t-lg text-sm font-medium transition-colors bg-gray-800 text-gray-400 hover:text-gray-200"
      aria-label="Open Meetings and Follow-ups"
    >
      <ClipboardCheck size={15} aria-hidden="true" />
      <span>Meetings</span>
    </button>
  );

  return (
    <>
      {tabStrip ? createPortal(button, tabStrip) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full border border-pink-400/30 bg-gray-950 px-4 py-3 text-sm font-semibold text-white shadow-xl shadow-black/30"
        >
          <ClipboardCheck size={17} /> Meetings
        </button>
      )}
      {open && <MeetingWorkspace onClose={() => setOpen(false)} />}
    </>
  );
}

export default MeetingPortal;
